import React, {
  ChangeEvent,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { ReferencePickerPlugin } from "@adsk/acc-reference-picker";
import FormField from "@adsk/alloy-react-form-field";
import { MagnifyingGlassIcon } from "@adsk/alloy-react-icon";
import { useLocale } from "@adsk/alloy-react-locale";
import TextInput from "@adsk/alloy-react-text-input";
import theme from "@adsk/alloy-react-theme";

import { AssetWithCategoryAndStatus } from "../../models";
import { useSearch } from "../shared/SearchContext";

const SEARCH_EXPRESSION_MIN_LENGTH = 2;
const SEARCH_EXPRESSION_MAX_LENGTH = 100;
const SHOW_ERROR_DELAY = 500;

const validateSearchTerm = (
  searchTerm: string,
  T: ReturnType<typeof useLocale>["T"]
): ReactNode => {
  if (!searchTerm.length) return undefined;

  if (searchTerm.length < SEARCH_EXPRESSION_MIN_LENGTH)
    return T.translate("SEARCH_EXPRESSION_TOO_SHORT", {
      min: SEARCH_EXPRESSION_MIN_LENGTH,
    });

  if (searchTerm.length > SEARCH_EXPRESSION_MAX_LENGTH)
    return T.translate("SEARCH_EXPRESSION_TOO_LONG", {
      max: SEARCH_EXPRESSION_MAX_LENGTH,
    });

  return undefined;
};

type AssetsPickerSearchBoxProps = React.ComponentProps<
  NonNullable<
    ReferencePickerPlugin<AssetWithCategoryAndStatus>["CustomPickerSearchBox"]
  >
>;

const AssetsPickerSearchBox: FC<AssetsPickerSearchBoxProps> = ({
  disabled,
  onSearchChange,
}) => {
  const { searchTerm, setSearchTerm } = useSearch();
  const [errorMessage, setErrorMessage] = useState<ReactNode>();
  const showErrorTimeoutId = useRef<number | undefined>(undefined);
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newErrorMessage = validateSearchTerm(e.target.value, T);

      setSearchTerm(e.target.value);
      onSearchChange(newErrorMessage ? "" : e.target.value);

      clearTimeout(showErrorTimeoutId.current);

      if (!errorMessage && newErrorMessage) {
        showErrorTimeoutId.current = window.setTimeout(() => {
          setErrorMessage(newErrorMessage);
        }, SHOW_ERROR_DELAY);
      } else if (errorMessage !== newErrorMessage) {
        setErrorMessage(newErrorMessage);
      }
    },
    [onSearchChange, setSearchTerm, T, errorMessage]
  );

  useEffect(
    // effect with just a clean up (note the double arrow fn)
    // to prevent the setTimeout callback to update the
    // errorMessage state after the component unmounted
    () => () => {
      setSearchTerm("");
      clearTimeout(showErrorTimeoutId.current);
    },
    [setSearchTerm]
  );

  return (
    <FormField
      error={errorMessage}
      errorVariant={FormField.ERROR_VARIANTS.CAPTION}
      style={{
        marginBottom: errorMessage ? "-14px" : "0px",
      }}
    >
      <TextInput
        error={Boolean(errorMessage)}
        style={{
          width: 300,
          "input:disabled": {
            background: theme.colors.white,
          },
        }}
        placeholder={T.translate("SEARCH")?.toString()}
        renderRightElement={(p) => <MagnifyingGlassIcon {...p} />}
        value={searchTerm}
        onChange={onChange}
        disabled={disabled}
      />
    </FormField>
  );
};

export default AssetsPickerSearchBox;
